import React from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Envelope, PaperPlaneTilt, PaperclipHorizontal } from '@phosphor-icons/react';

export const ClaimEmailsTab = ({
  emails, emailLoading, openComposeEmail,
}) => (
  <Card className="bg-white border border-slate-200 shadow-sm">
    <CardContent className="pt-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold font-['Outfit'] text-slate-800 flex items-center gap-2">
          <Envelope className="h-4 w-4 text-indigo-500" weight="duotone" /> Email Communication
        </h3>
        <Button size="sm" onClick={() => openComposeEmail()} disabled={emailLoading}
          className="bg-indigo-500 hover:bg-indigo-600 text-white gap-1.5 text-xs" data-testid="compose-email-btn">
          <PaperPlaneTilt className="h-3.5 w-3.5" /> Compose Email
        </Button>
      </div>
      {emails.length === 0 ? (
        <div className="text-center py-10 text-slate-400">
          <Envelope className="h-10 w-10 mx-auto text-slate-300 mb-2" />
          <p className="text-sm">No emails sent yet for this claim</p>
          <p className="text-xs text-slate-400 mt-1">Click "Compose Email" to send documents to TPA</p>
        </div>
      ) : (
        <div className="space-y-3">
          {emails.map(em => (
            <div key={em.id} className={`rounded-lg border p-4 ${em.direction === 'incoming' ? 'border-teal-200 bg-teal-50/50' : em.status === 'failed' ? 'border-red-200 bg-red-50/50' : 'border-slate-200 bg-white'}`}
              data-testid={`email-row-${em.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`text-[9px] border-0 ${em.direction === 'incoming' ? 'bg-teal-100 text-teal-700' : 'bg-indigo-100 text-indigo-700'}`}>
                      {em.direction === 'incoming' ? 'RECEIVED' : 'SENT'}
                    </Badge>
                    <Badge className={`text-[9px] border-0 ${em.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : em.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                      {em.status?.toUpperCase()}
                    </Badge>
                    <span className="text-[10px] text-slate-400">{new Date(em.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <p className="text-sm font-medium text-slate-800 truncate">{em.subject}</p>
                  <p className="text-xs text-slate-500 mt-0.5">To: {em.to} {em.cc ? `| CC: ${em.cc}` : ''}</p>
                  <p className="text-xs text-slate-500">By: {em.sent_by_name}</p>
                </div>
              </div>
              {em.body && (
                <div className="mt-2 p-2 bg-slate-50 rounded text-xs text-slate-600 whitespace-pre-wrap max-h-24 overflow-y-auto">
                  {em.body}
                </div>
              )}
              {em.attachment_names?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {em.attachment_names.map((name, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                      <PaperclipHorizontal className="h-3 w-3" /> {name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

export const ComposeEmailDialog = ({
  open, onOpenChange, emailTemplates, selectedTemplate, handleTemplateChange,
  emailForm, setEmailForm, emailAttachments, toggleAttachment,
  sendingEmail, handleSendEmail,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-['Outfit'] flex items-center gap-2">
          <Envelope className="h-5 w-5 text-indigo-500" /> Compose Claim Email
        </DialogTitle>
        <DialogDescription>Send claim documents to TPA / Insurance</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 pt-2">
        <div>
          <Label className="text-xs">Email Template</Label>
          <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
            <SelectTrigger className="border-slate-200" data-testid="template-select"><SelectValue placeholder="Choose a template..." /></SelectTrigger>
            <SelectContent>
              {emailTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">To (TPA Email) *</Label>
            <Input value={emailForm.to} onChange={e => setEmailForm(f => ({...f, to: e.target.value}))}
              placeholder="tpa@insurance.com" className="border-slate-200" data-testid="email-to" />
          </div>
          <div>
            <Label className="text-xs">CC (Patient Email)</Label>
            <Input value={emailForm.cc} onChange={e => setEmailForm(f => ({...f, cc: e.target.value}))}
              placeholder="patient@email.com" className="border-slate-200" data-testid="email-cc" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Subject *</Label>
          <Input value={emailForm.subject} onChange={e => setEmailForm(f => ({...f, subject: e.target.value}))}
            className="border-slate-200" data-testid="email-subject" />
        </div>
        <div>
          <Label className="text-xs">Body *</Label>
          <Textarea value={emailForm.body} onChange={e => setEmailForm(f => ({...f, body: e.target.value}))}
            rows={10} className="border-slate-200 resize-none text-sm font-mono" data-testid="email-body" />
        </div>
        {emailAttachments.length > 0 && (
          <div>
            <Label className="text-xs mb-2 block">Attachments (select documents to attach)</Label>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {emailAttachments.map(att => (
                <label key={att.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={emailForm.attachment_ids.includes(att.id)}
                    onChange={() => toggleAttachment(att.id)} className="rounded border-slate-300" />
                  <PaperclipHorizontal className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-xs text-slate-700">{att.name}</span>
                  {att.type && <span className="text-[9px] text-slate-400 bg-slate-100 px-1 rounded">{att.type}</span>}
                </label>
              ))}
            </div>
          </div>
        )}
        <Button onClick={handleSendEmail} disabled={sendingEmail || !emailForm.to || !emailForm.subject}
          className="w-full bg-gradient-to-r from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700 text-white gap-2"
          data-testid="send-email-submit-btn">
          <PaperPlaneTilt className="h-4 w-4" /> {sendingEmail ? 'Sending...' : 'Send Email'}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);
